import { HelperActionRows } from '@/components/aiTools/HelperActionRows';
/**
 * AssessmentLetter: the Assessment Letter tool page.
 *
 * The family letter sent after an assessment visit in place of the formal
 * report. A sibling of the Initial Assessment Drafter built from its
 * plumbing: the ONE client block (name, pronouns, recipients), the visit
 * notes input, the vetted recommendations library (picked BEFORE writing so
 * the letter carries them with their contact details), the brand voice and
 * signatory on the server, and the assessment draft persistence
 * (mode 'letter') for save, the drafts inbox, and the Word download.
 *
 * Two acts: compose (client block, letter form, the library) and review
 * (the input collapses to a chip; the letter is editable in place; Save
 * then Download Word).
 *
 * Pronouns follow the drafter's rules: a picked client's recorded pronouns
 * fill the select (a line seat picks from her own caseload), a bound client
 * needs a choice before the letter is written and before the library loads,
 * picks already chosen are re-worded when the choice changes, and after
 * writing a line names the set the letter was written with when the select
 * no longer matches it. The letter request still carries each pick as
 * { topic, text }.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canAuthorClinicalNotes, canUseAiTools, canReadEveryClient, canViewAnyClientRoster } from '@/lib/capabilities';
import { useOwnCaseloadPicks } from '@/lib/cmHomeApi';
import { useReresolvedPicks, type RecommendationSource } from '@/lib/recommendationText';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton, PRINT_PDF_LABEL } from '@/components/DocumentPrintPdfButton';
import { SaveToClientDocumentsButton } from '@/components/SaveToClientDocumentsButton';
import { ToolPageHeader } from '@/components/aiTools/ToolPageHeader';
import { ToolDraftingState } from '@/components/aiTools/ToolPageShell';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import {
  AssessmentLetterForm,
  type LetterRecommendationPick,
} from '@/components/aiTools/AssessmentLetterForm';
import { AssessmentLetterOutput } from '@/components/aiTools/AssessmentLetterOutput';
import {
  AssessmentClientBlock,
  boundClient,
  buildPronounChoice,
  EMPTY_PRONOUN_CUSTOM,
  pronounChoiceKey,
  pronounChoiceLabel,
  pronounCustomComplete,
  pronounsNeeded,
  type PronounCustomForms,
  type PronounPresetChoice,
  type PronounSource,
} from '@/components/aiTools/AssessmentClientBlock';
import { RecommendationPicker } from '@/components/aiTools/RecommendationPicker';
import { Button } from '@/components/primitives/Button';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { aiSaveKeyOf, type KeyedError } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import {
  aiToolsApi,
  type AssessmentFormInputSnapshot,
  type AssessmentLetterBody,
  type AssessmentLetterDraftResponse,
  type SpicePronounChoice,
} from '@/lib/api';
import { AI_TOOLS } from './AiTools';

const TOOL = AI_TOOLS.find((t) => t.slug === 'assessment-letter')!;

const CARD_CLS = 'rounded-card bg-paper p-6 shadow-sh2';
const EYEBROW_CLS = 'text-xs font-semibold uppercase tracking-[0.16em]';

/** Today's calendar date as ISO, from the browser clock (no UTC shift). */
export function todayLocalIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function wordCount(text: string): number {
  const t = text.trim();
  return t.length === 0 ? 0 : t.split(/\s+/).length;
}

const THEY_THEM: SpicePronounChoice = { preset: 'they_them' };

/** What a reload or an expired sign-in keeps of this page: the whole
 *  form, and the letter once written (src/lib/keptWork.ts). */
interface KeptLetter {
  notes: string;
  clientFirstName: string;
  clientLastName: string;
  recipients: string;
  pronounPreset: PronounPresetChoice;
  pronounCustom: PronounCustomForms;
  pronounSource: PronounSource | null;
  pickedClient: { id: string; firstName: string; lastName: string } | null;
  letterDate: string;
  reOverride: string | null;
  picks: LetterRecommendationPick[];
  letter: {
    response: AssessmentLetterDraftResponse;
    formInput: AssessmentFormInputSnapshot;
    proseChoice: SpicePronounChoice | null;
  } | null;
}

export function AssessmentLetter(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftResponse, setDraftResponse] = useState<AssessmentLetterDraftResponse | null>(null);
  const [lastFormInput, setLastFormInput] = useState<AssessmentFormInputSnapshot | null>(null);
  const [saveError, setSaveError] = useState<KeyedError | null>(null);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);
  // The saved draft's client: where Save to client documents files it.
  const [savedClientId, setSavedClientId] = useState<string | null>(null);
  const [inputExpanded, setInputExpanded] = useState(false);

  // The ONE client block: the whole letter reads from it.
  const [clientFirstName, setClientFirstName] = useState('');
  const [clientLastName, setClientLastName] = useState('');
  const [recipients, setRecipients] = useState('');
  const [pronounPreset, setPronounPreset] = useState<PronounPresetChoice>('');
  const [pronounCustom, setPronounCustom] = useState<PronounCustomForms>({ ...EMPTY_PRONOUN_CUSTOM });
  // Where the select's value came from; null whenever the select is ''.
  const [pronounSource, setPronounSource] = useState<PronounSource | null>(null);
  const pronounChoice = buildPronounChoice(pronounPreset, pronounCustom);
  const pronounsComplete = pronounCustomComplete(pronounPreset, pronounCustom);

  // A line seat (no all-clients read) looks the client up in her own
  // caseload, the drafter's condition.
  const usesOwnCaseload = canAuthorClinicalNotes(whoami) && !canReadEveryClient(whoami);
  const ownCaseload = useOwnCaseloadPicks(tenantSlug, usesOwnCaseload);
  // A lookup-picked client, bound while both names still match the pick.
  const [pickedClient, setPickedClient] = useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const bound = boundClient(pickedClient, clientFirstName, clientLastName);
  // A bound client needs a choice before the letter and the library.
  const pronounsRequired = bound !== null;
  const libraryNeedsPronouns = pronounsNeeded(pronounPreset, pronounCustom, pronounsRequired);
  // The pronoun choice in effect: the select, or they/them when nothing is
  // chosen and nothing needs a choice.
  const effectiveChoice: SpicePronounChoice | undefined =
    pronounChoice ?? (libraryNeedsPronouns ? undefined : THEY_THEM);
  // The pronoun choice the letter prose was written with.
  const [proseChoice, setProseChoice] = useState<SpicePronounChoice | undefined>(undefined);

  const handlePronounPresetChange = (v: PronounPresetChoice, source: PronounSource): void => {
    setPronounPreset(v);
    setPronounSource(v === '' ? null : source);
  };

  // Unbinding (a name edit after a pick) clears a choice that came from the
  // record; a care manager's own choice stays.
  const boundId = bound?.id ?? null;
  const lastBoundIdRef = useRef<string | null>(null);
  useEffect(() => {
    const wasBound = lastBoundIdRef.current !== null;
    lastBoundIdRef.current = boundId;
    if (wasBound && boundId === null && pronounSource === 'record') {
      handlePronounPresetChange('', 'record');
      setPronounCustom({ ...EMPTY_PRONOUN_CUSTOM });
    }
  }, [boundId]);

  const [letterDate, setLetterDate] = useState(todayLocalIso);
  // RE follows the client's name until the care manager edits it.
  const [reOverride, setReOverride] = useState<string | null>(null);
  const re = reOverride ?? `${clientFirstName.trim()} ${clientLastName.trim()}`.trim();

  // Vetted recommendations picked from the library for this letter, each
  // with its source so it follows a later pronoun change.
  const [picks, setPicks] = useState<LetterRecommendationPick[]>([]);
  const pickIdRef = useRef(0);
  const handleAddPick = (
    text: string,
    topic: string,
    _needsResources: boolean,
    source: RecommendationSource | undefined,
  ): void => {
    setPicks((prev) => [...prev, { id: `pick-${pickIdRef.current++}`, topic, text, ...source }]);
  };
  const {
    picksPending,
    currentKey: currentPronounKey,
    retry: retryPick,
  } = useReresolvedPicks({
    tenantSlug,
    firstName: clientFirstName,
    effectiveChoice,
    picks,
    setPicks,
  });
  const handleRemovePick = (id: string): void => {
    setPicks((prev) => prev.filter((p) => p.id !== id));
  };

  const handleResponseChange = (next: AssessmentLetterBody): void => {
    if (draftResponse === null) return;
    setDraftResponse({ ...draftResponse, ...next });
    setSavedDraftId(null);
  };

  /** The letter's save request, built from the screen at the press; null
   *  with no generated letter yet. */
  function saveRequestOf(): Parameters<typeof aiToolsApi.saveAssessmentDraft>[1] | null {
    if (!draftResponse || !lastFormInput) return null;
    const r = draftResponse;
    return {
        ai_invocation_id: r.ai_invocation_id,
        client_id: bound?.id ?? null,
        mode: 'letter',
        header: r.header,
        salutation: r.salutation,
        opening: r.opening,
        recommendations: r.recommendations,
        closing: r.closing,
        valediction: r.valediction,
        signature: r.signature,
        signatory_missing: r.signatory_missing,
        follow_up_questions: r.follow_up_questions,
        form_input: lastFormInput,
    };
  }

  // The request (and with it the client) travels as the variables.
  const saveMutation = useMutation({
    mutationFn: (request: Parameters<typeof aiToolsApi.saveAssessmentDraft>[1] | null) => {
      if (request === null) throw new Error('saveDraft called without a generated letter');
      return aiToolsApi.saveAssessmentDraft(tenantSlug, request);
    },
    onSuccess: (res, request) => {
      setSaveError(null);
      setSavedDraftId(res.id);
      setSavedClientId(request?.client_id ?? null);
    },
    onError: (e: unknown) => {
      setSavedDraftId(null);
      setSaveError(aiSaveKeyOf(e));
    },
  });

  // An expired sign-in keeps the written letter as edited, with the client
  // block and picks it was written for (src/lib/keptWork.ts). Before the
  // letter is written the notes field is the work, and it keeps itself.
  // A reload or an expired sign-in keeps the whole form: the client block,
  // the pronouns, the recipients, the date, the RE line, the picks, the
  // notes and, once written, the letter as edited (src/lib/keptWork.ts).
  // Restoring brings all of it back together.
  const [formNotes, setFormNotes] = useState('');
  const [keptNotes, setKeptNotes] = useState<string | undefined>(undefined);
  // A restore remounts the form so its notes start from the kept copy.
  const [formGeneration, setFormGeneration] = useState(0);
  const kept = useKeptWork<KeptLetter>(
    keptWorkKey(tenantSlug, 'assessment-letter', null),
    () => {
      if (savedDraftId !== null) return null;
      const typed =
        draftResponse !== null ||
        pickedClient !== null ||
        picks.length > 0 ||
        reOverride !== null ||
        [clientFirstName, clientLastName, recipients, formNotes].some((v) => v.trim() !== '');
      if (!typed) return null;
      return {
        notes: formNotes,
        clientFirstName,
        clientLastName,
        recipients,
        pronounPreset,
        pronounCustom,
        pronounSource,
        pickedClient,
        letterDate,
        reOverride,
        picks,
        letter:
          draftResponse === null || lastFormInput === null
            ? null
            : { response: draftResponse, formInput: lastFormInput, proseChoice: proseChoice ?? null },
      };
    },
    (k) => {
      setKeptNotes(k.notes);
      setFormGeneration((n) => n + 1);
      setClientFirstName(k.clientFirstName);
      setClientLastName(k.clientLastName);
      setRecipients(k.recipients);
      setPronounPreset(k.pronounPreset);
      setPronounCustom(k.pronounCustom);
      setPronounSource(k.pronounSource);
      setPickedClient(k.pickedClient);
      setLetterDate(k.letterDate);
      setReOverride(k.reOverride);
      setPicks(k.picks);
      if (k.letter !== null) {
        setDraftResponse(k.letter.response);
        setLastFormInput(k.letter.formInput);
        setProseChoice(k.letter.proseChoice ?? undefined);
        setInputExpanded(false);
      }
      setSavedDraftId(null);
    },
  );
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (savedDraftId !== null) kept.clear();
  }, [savedDraftId]);

  if (!canUseAiTools(whoami)) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <EmptyAiToolsTile />
      </main>
    );
  }

  const handleDraftReady = (
    response: AssessmentLetterDraftResponse,
    formInput: AssessmentFormInputSnapshot,
  ): void => {
    setDraftResponse(response);
    setLastFormInput(formInput);
    // The prose is written with the choice this letter recorded.
    setProseChoice(formInput.pronouns);
    setSaveError(null);
    setSavedDraftId(null);
    setInputExpanded(false);
  };

  const inputForm = (
    <div className="space-y-6">
      <AssessmentClientBlock
        tenantSlug={tenantSlug}
        canLookup={canViewAnyClientRoster(whoami)}
        caseload={ownCaseload}
        firstName={clientFirstName}
        lastName={clientLastName}
        recipient={recipients}
        pronounPreset={pronounPreset}
        pronounCustom={pronounCustom}
        onFirstNameChange={setClientFirstName}
        onLastNameChange={setClientLastName}
        onRecipientChange={setRecipients}
        onPronounPresetChange={handlePronounPresetChange}
        onPronounCustomChange={setPronounCustom}
        onClientPicked={setPickedClient}
        disabled={isGenerating}
        letter
      />
      <section className={CARD_CLS}>
        <span className={`${EYEBROW_CLS} text-sage-400`}>Your letter</span>
        <div className="mt-4">
          <AssessmentLetterForm
            key={formGeneration}
            keptNotes={keptNotes}
            onNotesChange={setFormNotes}
            tenantSlug={tenantSlug}
            recipients={recipients}
            clientFirstName={clientFirstName}
            clientLastName={clientLastName}
            pronouns={pronounChoice}
            pronounsComplete={pronounsComplete}
            pronounsRequired={pronounsRequired}
            picksPending={picksPending}
            letterDate={letterDate}
            onLetterDateChange={setLetterDate}
            re={re}
            onReChange={setReOverride}
            picks={picks}
            onRemovePick={handleRemovePick}
            currentPronounKey={currentPronounKey}
            onRetryPick={retryPick}
            onGeneratingChange={setIsGenerating}
            onDraftReady={handleDraftReady}
            hasDraft={draftResponse !== null}
          />
        </div>
      </section>
      <RecommendationPicker
        tenantSlug={tenantSlug}
        clientFirstName={clientFirstName}
        pronouns={pronounChoice}
        pronounsNeeded={libraryNeedsPronouns}
        onAddToReport={handleAddPick}
      />
    </div>
  );

  // ---- Act 1: compose. ----
  if (draftResponse === null) {
    return (
      <main
        className="mx-auto max-w-[1160px] px-5 pt-8 pb-20 md:px-10"
        data-testid="tool-shell-assessment-letter"
      >
        <ToolPageHeader tool={TOOL} tenantSlug={tenantSlug} />
        <div className="mx-auto mt-5 max-w-[720px]" data-testid="assessment-letter-stage-compose">
          {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
          {inputForm}
          {isGenerating && (
            <section className={`${CARD_CLS} mt-6`}>
              <ToolDraftingState />
            </section>
          )}
        </div>
      </main>
    );
  }

  // ---- Act 2: review + the finalize ladder. ----
  const notesWords = wordCount(lastFormInput?.notes ?? '');

  return (
    <main
      className="mx-auto max-w-[1160px] px-5 pt-8 pb-20 md:px-10"
      data-testid="tool-shell-assessment-letter"
    >
      <ToolPageHeader tool={TOOL} tenantSlug={tenantSlug} />
      {kept.restored && (
        <div className="mt-5">
          <KeptWorkNotice onDiscard={kept.discard} />
        </div>
      )}
      <div className="mt-[18px]" data-testid="assessment-letter-stage-review">
        <div
          className="flex flex-wrap items-center gap-3 rounded-pill border border-sage-800/[8%] bg-paper px-4 py-2 shadow-sh1"
          data-testid="assessment-letter-input-chip"
        >
          <span className="text-sm text-ink">
            <span className="font-semibold">Your notes</span>
            {' · '}
            {notesWords.toLocaleString()} {notesWords === 1 ? 'word' : 'words'}
          </span>
          {isGenerating && <span className="text-xs italic text-sage-400">Rewriting…</span>}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setInputExpanded((v) => !v)}
            aria-expanded={inputExpanded}
            data-testid="assessment-letter-input-chip-toggle"
          >
            {inputExpanded ? 'Collapse' : 'View / edit'}
          </Button>
        </div>
        {inputExpanded && (
          <div className="mt-4" data-testid="assessment-letter-input-expanded">
            {inputForm}
          </div>
        )}

        <div className="mt-6 space-y-4">
          {proseChoice !== undefined &&
            effectiveChoice !== undefined &&
            pronounChoiceKey(proseChoice) !== pronounChoiceKey(effectiveChoice) && (
              <p
                className="rounded-[12px] bg-sand px-3 py-2 text-sm text-sage-800"
                data-testid="assessment-pronoun-mismatch"
                aria-live="polite"
              >
                {`The letter was written with ${pronounChoiceLabel(proseChoice)}. Use "Write the letter" to update it.`}
              </p>
            )}
          <AssessmentLetterOutput response={draftResponse} onResponseChange={handleResponseChange} />

          <section className="space-y-3 pt-2" data-testid="assessment-letter-finalize-ladder">
            <HelperActionRows
              testId="assessment-letter-save-row"
              primary={
                savedDraftId !== null ? (
                  <>
                    <DownloadDocxButton
                      url={aiToolsApi.assessmentDownloadUrl(tenantSlug, savedDraftId)}
                      filename="assessment-letter.docx"
                      variant="primary"
                      testId="assessment-letter-download-link"
                    >
                      Download Word
                    </DownloadDocxButton>
                    <DocumentPrintPdfButton
                      url={aiToolsApi.assessmentDownloadUrl(tenantSlug, savedDraftId)}
                      filename="assessment-letter.docx"
                      testId="assessment-letter-print-pdf"
                    />
                    <SaveToClientDocumentsButton
                      tenantSlug={tenantSlug}
                      draftId={savedDraftId}
                      clientId={savedClientId}
                      testId="assessment-letter-save-to-documents"
                    />
                  </>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      disabled
                      data-testid="assessment-letter-download-disabled"
                    >
                      Download Word
                    </Button>
                    <Button variant="secondary" disabled data-testid="assessment-letter-print-pdf-disabled">
                      {PRINT_PDF_LABEL}
                    </Button>
                  </>
                )
              }
              secondary={
                <Button
                  variant="secondary"
                  onClick={() => saveMutation.mutate(saveRequestOf())}
                  disabled={
                    saveMutation.isPending ||
                    savedDraftId !== null ||
                    !pronounsComplete ||
                    picksPending
                  }
                  aria-label="Save draft"
                >
                  {savedDraftId !== null
                    ? '✓ Saved'
                    : saveMutation.isPending
                      ? 'Saving…'
                      : 'Save draft'}
                </Button>
              }
            />
            <p className="text-xs text-ink-soft" data-testid="assessment-letter-finalize-copy">
              {savedDraftId !== null ? (
                <>
                  Saved to your drafts inbox under Writing.{' '}
                  <Link
                    to={`/t/${tenantSlug}/ai-tools/drafts`}
                    className="text-sage-700 underline hover:text-sage-900"
                    data-testid="assessment-letter-saved-link"
                  >
                    Open the drafts inbox
                  </Link>
                </>
              ) : (
                'Saving files this letter to your drafts inbox under Writing and turns on the Word download.'
              )}
            </p>
            {saveError !== null && (
              <ErrorNotice
                surface="assessment_draft_save"
                code={saveError.key}
                vars={saveError.vars}
                data-testid="assessment-letter-save-error"
              />
            )}
          </section>
        </div>
      </div>
    </main>
  );
}
